import { useEffect, useState } from "react";
import { Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

const EMPTY = {
  hero_eyebrow: "", hero_title: "", hero_highlight: "", hero_subtext: "", hero_image: "", favicon: "",
  marquee: [], features: [], footer_text: "",
};

export default function SuperCms() {
  const [cms, setCms] = useState(null);
  const [marqueeText, setMarqueeText] = useState("");

  useEffect(() => {
    api.get("/admin/cms").then((r) => {
      const data = { ...EMPTY, ...r.data };
      setCms(data);
      setMarqueeText((data.marquee || []).join("\n"));
    }).catch((e) => toast.error(apiError(e)));
  }, []);

  if (!cms) return <div className="font-display font-bold animate-pulse" data-testid="cms-loading">Loading…</div>;

  const setFeature = (i, patch) => setCms({ ...cms, features: cms.features.map((f, j) => (j === i ? { ...f, ...patch } : f)) });

  const save = async () => {
    try {
      await api.put("/admin/cms", { ...cms, marquee: marqueeText.split("\n").map((s) => s.trim()).filter(Boolean) });
      toast.success("Homepage content saved — live immediately");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="super-cms-page" className="max-w-4xl">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="nb-label">Homepage CMS</span>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">Landing page content</h1>
        </div>
        <button onClick={save} data-testid="cms-save-btn" className="nb-btn bg-[#A7E4D3] px-5 py-2.5">
          <Save className="w-4 h-4" /> Save & publish
        </button>
      </div>

      <div className="mt-8 nb-card p-6 space-y-5">
        <h2 className="font-display font-bold text-lg">Hero</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="nb-label" htmlFor="cms-eyebrow">Eyebrow</label>
            <input id="cms-eyebrow" className="nb-input mt-2" data-testid="cms-hero-eyebrow" value={cms.hero_eyebrow} onChange={(e) => setCms({ ...cms, hero_eyebrow: e.target.value })} />
          </div>
          <div>
            <label className="nb-label" htmlFor="cms-title">Title line</label>
            <input id="cms-title" className="nb-input mt-2" data-testid="cms-hero-title" value={cms.hero_title} onChange={(e) => setCms({ ...cms, hero_title: e.target.value })} />
          </div>
          <div>
            <label className="nb-label" htmlFor="cms-highlight">Highlighted line</label>
            <input id="cms-highlight" className="nb-input mt-2" data-testid="cms-hero-highlight" value={cms.hero_highlight} onChange={(e) => setCms({ ...cms, hero_highlight: e.target.value })} />
          </div>
          <div>
            <label className="nb-label" htmlFor="cms-image">Hero image URL</label>
            <input id="cms-image" className="nb-input mt-2" data-testid="cms-hero-image" value={cms.hero_image} onChange={(e) => setCms({ ...cms, hero_image: e.target.value })} />
          </div>
          <div>
            <label className="nb-label" htmlFor="cms-favicon">Favicon URL (site-wide)</label>
            <input id="cms-favicon" className="nb-input mt-2" data-testid="cms-favicon" placeholder="https://…/favicon.png (square, 192px+)"
              value={cms.favicon} onChange={(e) => setCms({ ...cms, favicon: e.target.value })} />
            <p className="text-[11px] text-zinc-400 font-semibold mt-1">Leave blank to use the default MyTimetable icon.</p>
          </div>
        </div>
        <div>
          <label className="nb-label" htmlFor="cms-subtext">Subtext</label>
          <textarea id="cms-subtext" className="nb-input mt-2 min-h-[90px] resize-y" data-testid="cms-hero-subtext" value={cms.hero_subtext} onChange={(e) => setCms({ ...cms, hero_subtext: e.target.value })} />
        </div>
      </div>

      <div className="mt-6 nb-card p-6">
        <h2 className="font-display font-bold text-lg">Marquee items (one per line)</h2>
        <textarea className="nb-input mt-4 min-h-[110px] resize-y font-mono text-sm" data-testid="cms-marquee"
          value={marqueeText} onChange={(e) => setMarqueeText(e.target.value)} />
      </div>

      <div className="mt-6 nb-card p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">Feature cards</h2>
          <button type="button" data-testid="cms-add-feature"
            onClick={() => setCms({ ...cms, features: [...cms.features, { title: "New feature", desc: "", bg: "#A7E4D3" }] })}
            className="nb-btn bg-[#FDE68A] px-3 py-1.5 text-sm"><Plus className="w-4 h-4" /> Add</button>
        </div>
        <div className="mt-4 space-y-4">
          {cms.features.map((f, i) => (
            <div key={i} className="rounded-xl border-2 border-zinc-900 p-4 grid sm:grid-cols-[1fr_2fr_110px_40px] gap-3 items-center" data-testid={`cms-feature-${i}`}>
              <input className="nb-input" placeholder="Title" data-testid={`cms-feature-title-${i}`} value={f.title} onChange={(e) => setFeature(i, { title: e.target.value })} />
              <input className="nb-input" placeholder="Description" data-testid={`cms-feature-desc-${i}`} value={f.desc} onChange={(e) => setFeature(i, { desc: e.target.value })} />
              <input className="nb-input px-2" type="color" data-testid={`cms-feature-bg-${i}`} value={f.bg || "#A7E4D3"} onChange={(e) => setFeature(i, { bg: e.target.value })} />
              <button type="button" data-testid={`cms-feature-delete-${i}`} onClick={() => setCms({ ...cms, features: cms.features.filter((_, j) => j !== i) })}
                className="p-2 rounded-lg border-2 border-zinc-900 bg-red-100 hover:bg-red-200 transition-colors duration-150">
                <Trash2 className="w-4 h-4 text-red-700" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 nb-card p-6">
        <label className="nb-label" htmlFor="cms-footer">Footer text</label>
        <input id="cms-footer" className="nb-input mt-2" data-testid="cms-footer-text" value={cms.footer_text} onChange={(e) => setCms({ ...cms, footer_text: e.target.value })} />
      </div>
    </div>
  );
}
